import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';

type PlayerState = {
  playerId: string;
  name: string;
  health: number;
  maxHealth: number;
  isDead: boolean;
  respawnTime: number;
  kills: number;
  deaths: number;
  team: string | null;
};

class PlayerCombatController {
  game: any;
  localPlayer: PlayerState | null;
  remotePlayers: Map<string, PlayerState>;
  healthBarEl: HTMLElement | null;
  deathScreenEl: HTMLElement | null;
  killFeedEl: HTMLElement | null;
  _respawnTimer: number;
  _respawnDelay: number;
  _spawnPoint: Vector3;
  _invulnerable: boolean;
  _invulnerableTimer: number;
  _invulnerableDuration: number;
  _damageFlashEl: HTMLElement | null;
  _damageFlashTimeout: ReturnType<typeof setTimeout> | null;
  _lastHitTarget: any;

  constructor(game: any) {
    this.game = game;
    this.localPlayer = null;
    this.remotePlayers = new Map();
    this.healthBarEl = null;
    this.deathScreenEl = null;
    this.killFeedEl = null;
    this._respawnTimer = 0;
    this._respawnDelay = 5.0;
    this._spawnPoint = new Vector3(0, 2, 0);
    this._invulnerable = false;
    this._invulnerableTimer = 0;
    this._invulnerableDuration = 2.0;
    this._damageFlashEl = null;
    this._damageFlashTimeout = null;
    this._lastHitTarget = null;
  }

  init() {
    this.localPlayer = this._createPlayerState(
      this.game.playerSlot || 'local',
      this.game.playerName || 'Player'
    );
    this._createHealthBar();
    this._createDeathScreen();
    this._createKillFeed();
    this._createDamageFlash();
  }

  _createPlayerState(playerId: string, name: string): PlayerState {
    return {
      playerId,
      name,
      health: 100,
      maxHealth: 100,
      isDead: false,
      respawnTime: 0,
      kills: 0,
      deaths: 0,
      team: null,
    };
  }

  _createHealthBar() {
    let el = document.getElementById('combat-health-bar');
    if (!el) {
      el = document.createElement('div');
      el.id = 'combat-health-bar';
      el.className = 'combat-health-bar';
      el.innerHTML =
        '<div class="combat-health-fill" id="combat-health-fill"></div>' +
        '<div class="combat-health-text" id="combat-health-text">100</div>';
      document.body.appendChild(el);
    }
    this.healthBarEl = el;
  }

  _createDeathScreen() {
    let el = document.getElementById('death-screen');
    if (!el) {
      el = document.createElement('div');
      el.id = 'death-screen';
      el.className = 'death-screen';
      el.style.display = 'none';
      el.innerHTML =
        '<div class="death-screen-title">ELIMINATED</div>' +
        '<div class="death-screen-timer" id="death-screen-timer">5</div>' +
        '<div class="death-screen-subtitle">Respawning...</div>';
      document.body.appendChild(el);
    }
    this.deathScreenEl = el;
  }

  _createKillFeed() {
    let el = document.getElementById('kill-feed');
    if (!el) {
      el = document.createElement('div');
      el.id = 'kill-feed';
      el.className = 'kill-feed';
      document.body.appendChild(el);
    }
    this.killFeedEl = el;
  }

  _createDamageFlash() {
    let el = document.getElementById('damage-flash');
    if (!el) {
      el = document.createElement('div');
      el.id = 'damage-flash';
      el.className = 'damage-flash';
      el.style.display = 'none';
      document.body.appendChild(el);
    }
    this._damageFlashEl = el;
  }

  applyDamageToLocalPlayer(amount: number, attackerId: string) {
    if (!this.localPlayer || this.localPlayer.isDead || this._invulnerable) return;

    this.localPlayer.health = Math.max(0, this.localPlayer.health - amount);
    this._updateHealthUI();
    this._showDamageFlash();

    if (this.localPlayer.health <= 0) {
      this._onLocalPlayerDeath(attackerId);
    }
  }

  applyDamageToPlayer(playerId: string, amount: number, attackerId: string) {
    if (playerId === (this.localPlayer?.playerId || 'local')) {
      this.applyDamageToLocalPlayer(amount, attackerId);
      return;
    }

    const remote = this.remotePlayers.get(playerId);
    if (!remote || remote.isDead) return;

    remote.health = Math.max(0, remote.health - amount);

    if (remote.health <= 0) {
      this._onRemotePlayerDeath(playerId, attackerId);
    }
  }

  _onLocalPlayerDeath(killerId: string) {
    if (!this.localPlayer) return;

    this.localPlayer.isDead = true;
    this.localPlayer.deaths += 1;
    this._respawnTimer = this._respawnDelay;

    if (this.deathScreenEl) {
      this.deathScreenEl.style.display = 'flex';
    }

    this.addKillFeedEntry(killerId, this.localPlayer.playerId);

    if (this.game.multiplayerConnectionController?.socket) {
      const ws = this.game.multiplayerConnectionController.socket;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'player_death',
          playerId: this.localPlayer.playerId,
          killerId: killerId,
        }));
      }
    }
  }

  _onRemotePlayerDeath(playerId: string, killerId: string) {
    const remote = this.remotePlayers.get(playerId);
    if (!remote) return;

    remote.isDead = true;
    remote.deaths += 1;

    if (killerId === this.localPlayer?.playerId) {
      this.localPlayer.kills += 1;
    }

    this.addKillFeedEntry(killerId, playerId);
  }

  addKillFeedEntry(killerId: string, victimId: string) {
    if (!this.killFeedEl) return;

    const killerName = this._getPlayerName(killerId);
    const victimName = this._getPlayerName(victimId);

    const entry = document.createElement('div');
    entry.className = 'kill-feed-entry';
    entry.innerHTML =
      '<span class="kill-feed-killer">' + this._escapeHtml(killerName) + '</span>' +
      ' <span class="kill-feed-action">eliminated</span> ' +
      '<span class="kill-feed-victim">' + this._escapeHtml(victimName) + '</span>';

    this.killFeedEl.insertBefore(entry, this.killFeedEl.firstChild);

    setTimeout(() => {
      if (entry.parentNode) entry.parentNode.removeChild(entry);
    }, 5000);

    while (this.killFeedEl.childElementCount > 5) {
      const last = this.killFeedEl.lastChild;
      if (last) this.killFeedEl.removeChild(last);
    }
  }

  _getPlayerName(playerId: string): string {
    if (this.localPlayer && playerId === this.localPlayer.playerId) {
      return this.localPlayer.name;
    }
    const remote = this.remotePlayers.get(playerId);
    if (remote) return remote.name;
    return playerId;
  }

  _updateHealthUI() {
    if (!this.localPlayer) return;

    const fillEl = document.getElementById('combat-health-fill');
    const textEl = document.getElementById('combat-health-text');

    const percent = Math.max(0, Math.min(100, this.localPlayer.health));
    const color = percent > 60 ? '#64c882' : percent > 30 ? '#f0c040' : '#fc7459';

    if (fillEl) {
      fillEl.style.width = percent + '%';
      fillEl.style.background = color;
    }
    if (textEl) {
      textEl.textContent = String(Math.ceil(this.localPlayer.health));
    }
  }

  _showDamageFlash() {
    if (!this._damageFlashEl) return;

    this._damageFlashEl.style.display = 'block';

    if (this._damageFlashTimeout) {
      clearTimeout(this._damageFlashTimeout);
    }

    this._damageFlashTimeout = setTimeout(() => {
      if (this._damageFlashEl) {
        this._damageFlashEl.style.display = 'none';
      }
    }, 150);
  }

  heal(amount: number) {
    if (!this.localPlayer || this.localPlayer.isDead) return;
    this.localPlayer.health = Math.min(this.localPlayer.maxHealth, this.localPlayer.health + amount);
    this._updateHealthUI();
  }

  respawn() {
    if (!this.localPlayer) return;

    this.localPlayer.health = this.localPlayer.maxHealth;
    this.localPlayer.isDead = false;
    this._invulnerable = true;
    this._invulnerableTimer = this._invulnerableDuration;

    if (this.deathScreenEl) {
      this.deathScreenEl.style.display = 'none';
    }

    this._updateHealthUI();

    if (this.game.playerMovementController) {
      this.game.playerMovementController.teleport(this._spawnPoint);
    }
  }

  setSpawnPoint(position: Vector3) {
    this._spawnPoint = position.clone();
  }

  registerRemotePlayer(playerId: string, name: string) {
    this.remotePlayers.set(playerId, this._createPlayerState(playerId, name));
  }

  unregisterRemotePlayer(playerId: string) {
    this.remotePlayers.delete(playerId);
  }

  update(delta: number) {
    if (this._invulnerable) {
      this._invulnerableTimer -= delta;
      if (this._invulnerableTimer <= 0) {
        this._invulnerable = false;
      }
    }

    if (this.localPlayer && this.localPlayer.isDead) {
      this._respawnTimer -= delta;

      const timerEl = document.getElementById('death-screen-timer');
      if (timerEl) {
        timerEl.textContent = String(Math.ceil(this._respawnTimer));
      }

      if (this._respawnTimer <= 0) {
        this.respawn();
      }
    }
  }

  _escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  dispose() {
    if (this.healthBarEl && this.healthBarEl.parentNode) {
      this.healthBarEl.parentNode.removeChild(this.healthBarEl);
    }
    if (this.deathScreenEl && this.deathScreenEl.parentNode) {
      this.deathScreenEl.parentNode.removeChild(this.deathScreenEl);
    }
    if (this.killFeedEl && this.killFeedEl.parentNode) {
      this.killFeedEl.parentNode.removeChild(this.killFeedEl);
    }
    if (this._damageFlashEl && this._damageFlashEl.parentNode) {
      this._damageFlashEl.parentNode.removeChild(this._damageFlashEl);
    }
    if (this._damageFlashTimeout) {
      clearTimeout(this._damageFlashTimeout);
    }
    this.healthBarEl = null;
    this.deathScreenEl = null;
    this.killFeedEl = null;
    this._damageFlashEl = null;
    this.localPlayer = null;
    this.remotePlayers.clear();
  }
}

export { PlayerCombatController };
export type { PlayerState };
